import React from 'react';
import { MarshmallowGauge } from './MarshmallowGauge';
import { MarshmallowSkewer } from './MarshmallowSkewer';

interface MarshmallowSidebarProps {
  remaining: number;
  total: number;
  burned: number;
  resetDate: string | null;
  history: Array<{ sessionId: string; burned: number; date: string }>;
  isDepleted: boolean;
  isLoading: boolean;
  onBurnAnimationEnd?: () => void;
  onBuyMore?: () => void;
}

function formatResetDate(dateStr: string | null): string {
  if (!dateStr) return 'Unknown';
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function formatHistoryDate(dateStr: string): string {
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

const tips = [
  'Be specific to save marshmallows!',
  'Build page by page for more control.',
  'Marshmallows refill each billing cycle.',
  'Describe your business clearly for the best results.',
];

export const MarshmallowSidebar: React.FC<MarshmallowSidebarProps> = ({
  remaining,
  total,
  burned,
  resetDate,
  history,
  isDepleted,
  isLoading,
  onBurnAnimationEnd,
  onBuyMore,
}) => {
  const randomTip = tips[Math.floor(Date.now() / 60000) % tips.length];
  const percentage = total > 0 ? Math.round((remaining / total) * 100) : 0;

  if (isLoading) {
    return (
      <div className="hidden lg:flex w-72 border-r border-slate-800/50 flex-col items-center justify-center">
        <div className="w-8 h-8 border-2 border-orange-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="hidden lg:flex w-72 border-r border-slate-800/50 flex-col p-6 overflow-y-auto">
      {/* Header */}
      <div className="flex items-center gap-2 mb-6">
        <span className="text-lg">🔥</span>
        <h3 className="text-white font-semibold text-sm">Marshmallows</h3>
      </div>

      {/* Gauge */}
      <div className="flex justify-center mb-4">
        <MarshmallowGauge
          remaining={remaining}
          total={total}
          burned={burned}
          onBurnAnimationEnd={onBurnAnimationEnd}
        />
      </div>

      {/* Status text */}
      <div className="text-center mb-6">
        <p
          className={`text-sm font-medium ${
            isDepleted ? 'text-red-400' : percentage <= 25 ? 'text-yellow-400' : 'text-gray-400'
          }`}
        >
          {isDepleted ? 'Out of marshmallows!' : `${percentage}% remaining`}
        </p>
        <p className="text-gray-600 text-xs mt-1">Resets {formatResetDate(resetDate)}</p>
      </div>

      {/* Buy More CTA — always visible */}
      {onBuyMore && (
        <div
          className={
            isDepleted ? 'bg-red-950/30 border border-red-900/50 rounded-lg p-3 mb-6' : 'mb-6'
          }
        >
          {isDepleted ? (
            <p className="text-red-300 text-xs leading-relaxed mb-3">
              Your marshmallows are all toasted! Buy more to keep building, or wait for your next
              billing cycle.
            </p>
          ) : (
            <p className="text-gray-500 text-xs leading-relaxed mb-3">
              Need more? Top up your balance any time.
            </p>
          )}
          <button
            onClick={onBuyMore}
            className="w-full py-2 px-3 bg-orange-500 hover:bg-orange-600 text-white text-xs font-semibold rounded-lg transition-colors"
          >
            Buy More Marshmallows
          </button>
        </div>
      )}

      {/* Recent usage */}
      {history.length > 0 && (
        <div className="mb-6">
          <h4 className="text-gray-500 text-xs font-medium uppercase tracking-wider mb-3">
            Recent Usage
          </h4>
          <div className="space-y-2">
            {history.map((entry, i) => (
              <div key={entry.sessionId || i} className="flex items-center justify-between text-xs">
                <span className="text-gray-500">{formatHistoryDate(entry.date)}</span>
                <span className="inline-flex items-center gap-1 text-orange-400 font-medium">
                  -{entry.burned}
                  <MarshmallowSkewer className="w-3 h-3" />
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Tip */}
      <div className="mt-auto pt-4 border-t border-slate-800/50">
        <h4 className="text-gray-500 text-xs font-medium uppercase tracking-wider mb-2">Tip</h4>
        <p className="text-gray-400 text-xs leading-relaxed italic">"{randomTip}"</p>
      </div>
    </div>
  );
};
